import React, { useEffect, useState } from 'react';
import { useSite } from '../../context/SiteContext';
import { adminCreatePricing, adminUpdatePricing, adminDeletePricing } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { Plus, Trash2, Save, X, Pencil, Loader2, Sparkles } from 'lucide-react';
import { I18nInput, bulkTranslate } from './I18nInput';
import { pickI18n } from '../../i18n/helpers';

const empty = {
  order: 0,
  service: { es: '', en: '' },
  modality: { es: '', en: '' },
  rate: { es: '', en: '' },
  cta: { es: '', en: '' },
};

const Field = ({ label, children, full }) => (
  <label className={`block ${full ? 'sm:col-span-2' : ''}`}>
    <span className="block text-[11px] uppercase tracking-widest text-white/50 mb-2">{label}</span>
    {children}
  </label>
);

const AdminPricing = () => {
  const { pricing, reload } = useSite();
  const { toast } = useToast();
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);

  useEffect(() => {
    if (editing === null) setForm(empty);
    else if (editing === 'new') setForm({ ...empty, order: (pricing?.length || 0) + 1 });
    else {
      const p = pricing.find((x) => x.id === editing);
      if (p) setForm(p);
    }
  }, [editing, pricing]);

  const save = async () => {
    setSaving(true);
    try {
      if (editing === 'new') await adminCreatePricing(form);
      else await adminUpdatePricing(editing, form);
      await reload();
      toast({ title: 'Tarifa guardada' });
      setEditing(null);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'No se pudo guardar' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('¿Eliminar esta tarifa?')) return;
    await adminDeletePricing(id);
    await reload();
    toast({ title: 'Eliminado' });
  };

  const translateAll = async () => {
    setTranslating(true);
    try {
      await bulkTranslate([
        { value: form.service, set: (v) => setForm((f) => ({ ...f, service: v })) },
        { value: form.modality, set: (v) => setForm((f) => ({ ...f, modality: v })) },
        { value: form.rate, set: (v) => setForm((f) => ({ ...f, rate: v })) },
        { value: form.cta, set: (v) => setForm((f) => ({ ...f, cta: v })) },
      ], toast);
    } finally {
      setTranslating(false);
    }
  };

  return (
    <div>
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#00e0c7] mb-3">Modelo de Precios</div>
          <h1 className="font-display text-4xl text-white font-light">Tarifas</h1>
        </div>
        {!editing && (
          <button onClick={() => setEditing('new')} className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-[#00e0c7] text-[#0d1b2a] font-semibold text-sm">
            <Plus className="w-4 h-4" /> Nueva tarifa
          </button>
        )}
      </div>

      {editing ? (
        <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-6">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <h2 className="text-white text-lg">{editing === 'new' ? 'Nueva tarifa' : 'Editar tarifa'}</h2>
            <div className="flex items-center gap-2">
              <button onClick={translateAll} disabled={translating} className="inline-flex items-center gap-2 px-4 h-9 rounded-full border border-[#00e0c7]/30 bg-[#00e0c7]/10 text-[#9be8dc] hover:bg-[#00e0c7]/20 text-xs font-medium transition-colors disabled:opacity-50">
                {translating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                Traducir a EN
              </button>
              <button onClick={() => setEditing(null)} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Orden"><input type="number" className="ldc-input" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} /></Field>
            <div><I18nInput label="Servicio" value={form.service} onChange={(v) => setForm({ ...form, service: v })} /></div>
            <div className="sm:col-span-2"><I18nInput label="Formato / Modalidad" value={form.modality} onChange={(v) => setForm({ ...form, modality: v })} /></div>
            <div className="sm:col-span-2"><I18nInput label="Tarifa Base" value={form.rate} onChange={(v) => setForm({ ...form, rate: v })} /></div>
            <div className="sm:col-span-2"><I18nInput label="Texto del botón (CTA)" value={form.cta} onChange={(v) => setForm({ ...form, cta: v })} /></div>
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button onClick={() => setEditing(null)} className="px-5 h-11 rounded-full border border-white/10 text-white/70 text-sm hover:bg-white/5">Cancelar</button>
            <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-[#00e0c7] text-[#0d1b2a] font-semibold text-sm disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {(pricing || []).map((p) => (
            <div key={p.id} className="p-5 rounded-2xl border border-white/8 bg-white/[0.02] grid sm:grid-cols-12 gap-3 items-center">
              <div className="sm:col-span-3 text-white font-medium">{pickI18n(p.service, 'es')}</div>
              <div className="sm:col-span-4 text-white/55 text-sm">{pickI18n(p.modality, 'es')}</div>
              <div className="sm:col-span-3 text-[#9be8dc] text-sm">{pickI18n(p.rate, 'es')}</div>
              <div className="sm:col-span-2 flex justify-end gap-1">
                <button onClick={() => setEditing(p.id)} className="p-2 text-white/50 hover:text-[#00e0c7]"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => remove(p.id)} className="p-2 text-white/50 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminPricing;
